import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

// A standard, royalty-free "positive notification" chime (Mixkit SFX 2018).
// Playing a real audio file (rather than a synthesized WebAudio tone) gives
// a much louder and more familiar alert.
const NOTIF_SRC = "/sounds/notification.mp3";
// Reuse a single <audio> element and rewind on each play so back-to-back
// messages don't overlap awkwardly and there's zero fetch latency.
let _notifAudio = null;
function getNotifAudio() {
  if (_notifAudio) return _notifAudio;
  try {
    _notifAudio = new Audio(NOTIF_SRC);
    _notifAudio.preload = "auto";
    _notifAudio.volume = 1.0; // maximum the browser allows
  } catch { /* ignore */ }
  return _notifAudio;
}

function playChime() {
  try {
    const a = getNotifAudio();
    if (!a) return;
    // Rewind so rapid successive alerts always trigger.
    try { a.currentTime = 0; } catch { /* ignore */ }
    a.volume = 1.0;
    const p = a.play();
    if (p && typeof p.catch === "function") {
      // Autoplay policies may reject the play until the user has interacted
      // with the page. In that case, fall back to a synthesized tone so the
      // user still gets *some* audible cue.
      p.catch(() => playFallbackChime());
    }
  } catch {
    playFallbackChime();
  }
}

// Synthesized fallback — high-pitched two-tone bell, boosted through a compressor.
function playFallbackChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    if (ctx.state === "suspended") { try { ctx.resume(); } catch { /* ignore */ } }
    const now = ctx.currentTime;
    const master = ctx.createGain();
    master.gain.value = 2.8;
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -14; comp.knee.value = 6; comp.ratio.value = 8;
    comp.attack.value = 0.002; comp.release.value = 0.12;
    master.connect(comp).connect(ctx.destination);
    const env = ctx.createGain();
    env.gain.setValueAtTime(0.0001, now);
    env.gain.exponentialRampToValueAtTime(1.0, now + 0.015);
    env.gain.exponentialRampToValueAtTime(0.35, now + 0.18);
    env.gain.exponentialRampToValueAtTime(0.0001, now + 0.6);
    env.connect(master);
    const oscA = ctx.createOscillator();
    oscA.type = "triangle";
    oscA.frequency.setValueAtTime(1568, now);
    oscA.frequency.setValueAtTime(2093, now + 0.14);
    oscA.connect(env);
    oscA.start(now); oscA.stop(now + 0.62);
    oscA.onended = () => { try { ctx.close(); } catch { /* ignore */ } };
  } catch { /* silent */ }
}

/** App-wide chat notifier: opens a lightweight WS purely to chime + toast
 *  for incoming messages. Runs alongside the Chat page's own socket. */
export default function ChatNotifier() {
  const { user } = useAuth();
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const [muted, setMuted] = useState(() => localStorage.getItem("chat_muted") === "1");
  const mutedRef = useRef(muted);
  const namesRef = useRef({});

  useEffect(() => { mutedRef.current = muted; }, [muted]);

  // Refresh mute flag when toggled elsewhere (Chat page)
  useEffect(() => {
    const onStorage = (e) => { if (e.key === "chat_muted") setMuted(e.newValue === "1"); };
    const onCustom = () => setMuted(localStorage.getItem("chat_muted") === "1");
    window.addEventListener("storage", onStorage);
    window.addEventListener("chat-mute-changed", onCustom);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("chat-mute-changed", onCustom);
    };
  }, []);

  // Preload names lookup for nicer toasts
  useEffect(() => {
    if (!user) return;
    api.get("/employees").then((r) => {
      const map = {};
      r.data.forEach((e) => { map[e.id] = e.full_name; });
      namesRef.current = map;
    }).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      const token = localStorage.getItem("prism_token");
      if (!token || !BACKEND) return;
      const wsUrl = BACKEND.replace(/^http/, "ws") + `/api/ws/chat?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onclose = () => {
        if (!cancelled) reconnectRef.current = setTimeout(connect, 2500);
      };
      ws.onerror = () => { try { ws.close(); } catch { /* ignore */ } };
      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (data.event !== "message.new") return;
          const m = data.message;
          if (!m || m.sender_id === user.id) return;
          // Tell TopNav to refresh the unread badge
          window.dispatchEvent(new Event("chat-unread-changed"));
          if (mutedRef.current) return;
          playChime();
          const name = namesRef.current[m.sender_id] || "Someone";
          const preview = m.content?.slice(0, 80) || (m.attachment ? `📎 ${m.attachment.filename}` : "sent a message");
          // Suppress toast if already on chat page (user sees the message live)
          if (window.location.pathname !== "/chat") {
            toast(`${name}: ${preview}`, { duration: 3500 });
          }
        } catch { /* ignore */ }
      };
    };
    connect();
    return () => {
      cancelled = true;
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      try { wsRef.current?.close(); } catch { /* ignore */ }
    };
  }, [user]);

  return null;
}
